(ns frontend.view.nev.exit-opportunity.view
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.field :as field]
   [common.schema.entity-type-names :as entity-type-names]
   [common.util :as cu]
   [frontend.actions.common :as common-actions]
   [frontend.db :as app-db]
   [frontend.domain.exit-opportunity.actions :as exit-opportunity-actions]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.view.entity-viewers.components :as nev-components]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [frontend.view.entity-viewers.modules.check-list.view :as check-list-module]
   [frontend.view.entity-viewers.modules.liabilities :as liabilities]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.modals.change-pipeline.view :as change-pipeline]
   [frontend.view.modals.curtains.exit-opportunity.lost-curtain :as loss-curtain]
   [frontend.view.modals.curtains.exit-opportunity.on-hold-curtain :as on-hold-curtain]
   [frontend.view.modals.events :as modals]
   [frontend.view.nev.events :as events]
   [frontend.view.not-found :as not-found]
   [frontend.view.syncing-data :as syncing]
   [reagent.core :as r]))

(defn toolbar-buttons
  [{:keys [entity]}]
  (let [entity-id (:db/id entity)
        flagged? (contains? (:account/flagged-entities @session/account)
                            entity-id)]
    (concat
     []
     (when-not (session/is-read-only-user?)
       [(zap-toolbar/dropdown-button
         {:icon      :fa-star
          :on-select (partial common-actions/toggle-flag entity flagged?)
          :ord       1
          :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-check
          :label            (loc/tr :won)
          :custom-style     "zap-btn__solid__green"
          :dropdown-color   "green"
          :dropdown-action? (not= (:exit-opportunity/status entity) :exit-opportunity.status/won)
          :on-select        (fn [_e]
                              (exit-opportunity-actions/won! {:entity entity}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-ban
          :label            (loc/tr :lost)
          :custom-style     "zap-btn__solid__salmon"
          :dropdown-color   "salmon"
          :dropdown-action? (not= (:exit-opportunity/status entity) :exit-opportunity.status/lost)
          :on-select        (fn [_e]
                              (modals/open-modal!
                               loss-curtain/modal-key
                               {:exit-opportunity-id (:db/id entity)}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-pause
          :label            (loc/tr :put-on-hold)
          :custom-style     "zap-btn__solid__yellow"
          :dropdown-color   "yellow"
          :dropdown-action? (not= (:exit-opportunity/status entity)
                                  :exit-opportunity.status/on-hold)
          :on-select        (fn [_e]
                              (modals/open-modal!
                               on-hold-curtain/modal-key
                               {:exit-opportunity-id (:db/id entity)}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-redo-alt
          :label            (loc/tr :activate)
          :custom-style     "zap-btn__solid__blue"
          :dropdown-color   "blue"
          :dropdown-action? (not= (:exit-opportunity/status entity)
                                  :exit-opportunity.status/active)
          :hidden?          (fn []
                              (not (some #{(:exit-opportunity/status entity)}
                                         [:exit-opportunity.status/on-hold
                                          :exit-opportunity.status/lost])))
          :on-select        (fn []
                              (exit-opportunity-actions/resurrect! entity))})])

     (when (and (not (:common/disabled? entity))
                (not (session/is-read-only-user?)))
       [(zap-toolbar/delete-button entity [:exit-opportunities-grid])])

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(def view-id :exit-opportunity)

(defn module-collapsed-path
  [module-id]
  [view-id :modules :module-config module-id
   :collapsed?])

(defn checklist-modules
  [{:keys [tenant-schema pipeline entity-type entity-id]}]
  (->> pipeline
       :pipeline/checklist-configurations
       (keep (fn [checklist-id]
               (let [checklist-entity (session/get-entity (or (:db/id checklist-id)
                                                              checklist-id))
                     module-id (keyword (str "check-list-" checklist-id))]
                 (when (= (:check-list/target-entity-type checklist-entity)
                          entity-type)
                   {:module-id        module-id
                    :header           (:check-list/label checklist-entity)
                    :collapsed?       @(r/track session/get-view-config
                                                (module-collapsed-path module-id))
                    :save-module-collapsed! (r/partial events/save-module-collapsed!
                                                       entity-type)
                    :start-collapsed? true
                    :header-tools     [check-list-module/header-view
                                       tenant-schema entity-id checklist-id]
                    :content          [:div
                                       [check-list-module/view
                                        {:parent-entity-id entity-id
                                         :checklist-entity-id checklist-id
                                         :fields [{:attribute :role/guest-buy-side
                                                   :label     (loc/tr :buy-side-guests)}]}]]}))))
       doall))

(defn custom-save!
  [{:keys [exit-opportunity original-pipeline-id]}
   attribute new-value on-success! on-error!]
  (when (= attribute :exit-opportunity/pipeline)
    (modals/open-modal!
     change-pipeline/modal-key
     {:ofs-opportunity exit-opportunity
      :selected-pipeline-id new-value
      :original-pipeline-id original-pipeline-id
      :on-close!       on-success!})
    ;; call pipeline select error! to revert select value
    (on-error!)
    true))

(defn associated-entities-header
  [{:keys [target-type]}]
  (if (= target-type :entity.type/investor)
    (loc/tr :associated-buyers)
    (loc/tr :associated-
            [(cu/str-trim-lcase
              (entity-type-names/get-plural target-type :en))])))

(defn main-view
  [{:keys [state-atom error-atom notification-atom events-pane-atom
           view-state-atom entity-atom temp-atom
           exit-opportunity pipeline _buyer header-fields check-list-fields
           capman-tenant?]}]
  (let [tenant-schema @session/tenant-schema
        exit-opportunity-id (:db/id exit-opportunity)
        original-pipeline-id (:exit-opportunity/pipeline exit-opportunity)
        buyer-id (:exit-opportunity/buyer exit-opportunity)
        entity-type (:entity/type exit-opportunity)]
    [:<>
     (when-not (session/is-guest?)
       [zap-toolbar/nev-toolbar
        {:state           state-atom
         :label           (r/partial zap-toolbar/toolbar-back
                                     [:exit-opportunities-grid]
                                     (loc/tr :back-to/exit-opportunities-grid))
         :actions         (toolbar-buttons {:entity exit-opportunity})
         :dropdown-toggle [:span (loc/tr :actions)
                           [:i.fas.fa-angle-down.ml-2]]
         :dropdown-toggle-class-name
         "zap-button zap-button--primary-btn actions-dropdown"}])
     [:div.entity__container
      (when @error-atom
        [nev-components/error-bar error-atom])
      (when @notification-atom
        [nev-components/notification-bar notification-atom])
      [:div.entity
       [:div
        {:class "w-full max-w-[1920px] mx-auto p-4"}
        [nev-components/owner-stage-rating-module
         {:entity-cursor          entity-atom
          :view-state             view-state-atom
          :entity                 exit-opportunity
          :entity-stage-attribute :exit-opportunity/stage
          :stage-entity-type      :entity.type/exit-opportunity-stage
          :stage-pipeline-id      original-pipeline-id
          :stage-pipeline-attribute :exit-opportunity-stage/pipeline
          :owner-attribute        :exit-opportunity/owner
          :members-attribute      :common/team-members}]
        [nev-components/header-module
         {:component-state temp-atom
          :related-tags-entity-id buyer-id
          :view-state      view-state-atom
          :entity          exit-opportunity
          :pipeline-id     original-pipeline-id
          :selected-fields header-fields}]
        [:div.md:grid.grid-cols-10.gap-4
         [:div
          (if (session/event-pane-disabled?)
            {:class "col-span-10"}
            {:class "md:col-span-5 xl:col-span-6"})
          [:div.entity-left
           [modules/view
            {:view-id  view-id
             :ordering @(r/track session/get-view-config
                                 [view-id :modules :ordering])
             :save-modules-order! (r/partial events/save-modules-order!
                                             entity-type)
             :modules
             (concat
              [{:module-id  :exit-opportunity-information
                :header     (loc/tr :nev-module/about-this-entity-type
                                    [(string/lower-case (loc/tr entity-type))])
                :collapsed? @(r/track session/get-view-config
                                      (module-collapsed-path
                                       :exit-opportunity-information))
                :save-module-collapsed! (r/partial events/save-module-collapsed!
                                                   :entity.type/exit-opportunity)
                :content
                [:div
                 [entity-editor/view
                  {:state-atom    state-atom
                   :entity-id     exit-opportunity-id
                   :view-id       view-id
                   :module-id     :exit-opportunity-information
                   :custom-save!  (r/partial
                                   custom-save!
                                   {:exit-opportunity exit-opportunity
                                    :original-pipeline-id original-pipeline-id})
                   :remove-fields (concat [:exit-opportunity/name
                                           :common/team-members
                                           :exit-opportunity/buyer
                                           :exit-opportunity/target-holding
                                           :common/network-drive
                                           :common/named-network-drives]
                                          (when (tenant-customizations/capman?)
                                            [:exit-opportunity/pipeline])
                                          (field/entity-tag-fields tenant-schema
                                                                   entity-type)
                                          header-fields
                                          check-list-fields)
                   :ordering      @(r/track
                                    session/get-view-config
                                    [view-id :modules :module-config
                                     :exit-opportunity-information :ordering])}]]}
               (let [module-id :exit-buyer
                     order-config-path [view-id :modules :module-config
                                        module-id :ordering]
                     module-label (loc/tr :nev-module/exit-opportunity-buyer)
                     collapsed-path (module-collapsed-path module-id)
                     collapsed? @(r/track
                                  session/get-view-config collapsed-path)
                     header-remove-tooltip (when capman-tenant?
                                             (loc/tr :remove- [(loc/tr :investor-lc)]))
                     remove-confirmation-body (when capman-tenant?
                                                (loc/tr :remove- [(loc/tr :investor-lc)]))
                     header-tools
                     (when (and (:exit-opportunity/buyer exit-opportunity)
                                (not collapsed?)
                                (not (session/is-read-only-user?)))
                       [:span.grow.flex.justify-end
                        [nev-components/remove-attribute-icon
                         {:entity            exit-opportunity
                          :attribute         :exit-opportunity/buyer
                          :tooltip-text      header-remove-tooltip
                          :confirmation-body remove-confirmation-body}]])]
                 (nev-components/entity-relation-module
                  {:state-atom          state-atom
                   :module-id           module-id
                   :view-id             view-id
                   :ordering            @(r/track
                                          session/get-view-config
                                          order-config-path)
                   :order-config-path   order-config-path
                   :module-label        module-label
                   :entity              exit-opportunity
                   :header-tools        header-tools
                   :forced-entity-types [:entity.type/investor]
                   :pipeline-id         original-pipeline-id
                   :relationship-attribute :exit-opportunity/buyer}))
               (let [module-id :exit-target-holding
                     order-config-path [view-id :modules :module-config
                                        module-id :ordering]
                     module-label (loc/tr :nev-module/exit-opportunity-target-holding)
                     collapsed-path (module-collapsed-path module-id)
                     collapsed? @(r/track
                                  session/get-view-config collapsed-path)
                     header-remove-tooltip (when capman-tenant?
                                             (loc/tr :remove- [(loc/tr :target-holding-lc)]))
                     remove-confirmation-body (when capman-tenant?
                                                (loc/tr :remove- [(loc/tr :target-holding-lc)]))
                     header-tools
                     (when (and (:exit-opportunity/target-holding exit-opportunity)
                                (not collapsed?)
                                (not (session/is-read-only-user?)))
                       [:span.grow.flex.justify-end
                        [nev-components/remove-attribute-icon
                         {:entity            exit-opportunity
                          :attribute         :exit-opportunity/target-holding
                          :tooltip-text      header-remove-tooltip
                          :confirmation-body remove-confirmation-body}]])]
                 (nev-components/entity-relation-module
                  {:state-atom          state-atom
                   :module-id           module-id
                   :view-id             view-id
                   :ordering            @(r/track
                                          session/get-view-config
                                          order-config-path)
                   :order-config-path   order-config-path
                   :module-label        module-label
                   :entity              exit-opportunity
                   :header-tools        header-tools
                   :forced-entity-types [:entity.type/holding]
                   :pipeline-id         original-pipeline-id
                   :override-editor-types {:holding/name :editor.type/link}
                   :relationship-attribute :exit-opportunity/target-holding}))]
              (when-not (session/is-guest?)
                (associated-entities/modules
                 {:entity-id exit-opportunity-id
                  :header-fn associated-entities-header
                  :save-module-collapsed! events/save-module-collapsed!
                  :module-collapsed-path module-collapsed-path}))

              ;;TODO use more generic pre-X-checks feature when applicable
              (when (session/has-feature-access? :feature/pre-investment-checks)
                (let [module-id :pre-exit-checks
                      order-config-path [view-id :modules :module-config module-id :entity.type/investor :ordering]
                      _collapsed-path (module-collapsed-path module-id)]
                  [(nev-components/pre-exit-checks-module
                    {:state-atom             state-atom
                     :module-id              module-id
                     :view-id                view-id
                     :ordering               @(r/track
                                               session/get-view-config
                                               order-config-path)
                     :order-config-path      order-config-path
                     :module-label           (loc/tr :nev-module/pre-exit-checks)
                     :entity                 exit-opportunity
                     :relationship-attribute :exit-opportunity/buyer})]))

              (when (session/decisions?)
                (let [module-id :exit-decisions]
                  [(nev-components/decisions-module
                    {:module-id              module-id
                     :view-id                view-id
                     :module-label           (loc/tr :nev-module/exit-decisions)
                     :entity                 exit-opportunity})]))

              (when (or (session/counterparty-diligence?)
                        (session/counterparty-diligence-admin?))
                (let [module-id :counterparty-diligences
                      order-config-path [view-id :modules :module-config module-id :ordering]]
                  [(nev-components/counterparty-diligences-module
                    {:state-atom             state-atom
                     :module-id              module-id
                     :view-id                view-id
                     :ordering               (get-in
                                              @session/view-config
                                              order-config-path)
                     :order-config-path      order-config-path
                     :module-label           (loc/tr :counterparty-diligences)
                     :entity                 exit-opportunity})]))

              (when (and @(r/track session/liabilities?)
                         (f-permissions/field-read-access?
                          :common/liabilities
                          :entity.type/exit-opportunity
                          original-pipeline-id))
                (let [module-id :exit-liabilities
                      view-id view-id
                      header (loc/tr :exit-liabilities)
                      write-access? (f-permissions/field-write-access?
                                     :common/liabilities
                                     :entity.type/exit-opportunity
                                     original-pipeline-id)
                      module-collapsed-path [view-id :modules :module-config module-id :collapsed?]]
                  (liabilities/module
                   {:module-id module-id
                    :header header
                    :entity-id exit-opportunity-id
                    :show-create? write-access?
                    :show-delete? write-access?
                    :read-only? (not write-access?)
                    :save-module-collapsed! nev-components/save-module-collapsed!
                    :module-collapsed-path module-collapsed-path})))

              ;;TODO feature check, also missing from other entity-viewers:
              (when (not (session/is-guest?))
                (checklist-modules {:pipeline pipeline
                                    :entity-type entity-type
                                    :tenant-schema tenant-schema
                                    :entity-id exit-opportunity-id}))

              (when (session/has-feature-access? :feature/entity-esign)
                (let [module-id :entity-esign]
                  [(nev-components/entity-esign-module
                    {:view-id      view-id
                     :module-id    module-id
                     :module-label (loc/tr :nev-module/entity-esign)
                     :entity       exit-opportunity})]))

              (when (and (not (session/is-mobile?))
                         (f-permissions/field-read-access?
                          :common/attachments
                          :entity.type/exit-opportunity
                          (->> exit-opportunity
                               :exit-opportunity/pipeline)))
                [(nev-components/attachments-module view-id
                                                    :exit-opportunity-attachments
                                                    exit-opportunity)]))}]]]
         (when-not (session/event-pane-disabled?)
           [:div  {:class "md:col-span-5 xl:col-span-4"}
            [event-pane/view
             {:state       events-pane-atom
              :entity-ids  [exit-opportunity-id]
              :module-args {:state-atom state-atom
                            :view-id    view-id}}]])]]]]]))

(defn resolve-check-list-fields
  [pipeline exit-opportunity]
  (->> pipeline
       :pipeline/checklist-configurations
       (mapcat (fn [checklist-id]
                 (let [checklist-id (or (:db/id checklist-id)
                                        checklist-id)
                       checklist-entity (session/get-entity
                                         checklist-id)]
                   (when (= (:check-list/target-entity-type checklist-entity)
                            (:entity/type exit-opportunity))
                     (->> checklist-entity
                          :check-list/configuration)))))
       (distinct)))

(defn exit-opportunity-view
  [{:keys [exit-opportunity-id] :as _view-args}]
  (let [state-atom (r/cursor app-db/app-state [:views :nev :exit-opportunity])
        initialized? (r/atom false)
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [exit-opportunity-id])
        exit-opportunity (session/get-entity exit-opportunity-id)
        original-pipeline-id (:exit-opportunity/pipeline exit-opportunity)
        capman-tenant? (tenant-customizations/capman?)]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :exit-opportunity-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom exit-opportunity-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true)
        (swap! session/selected-pipelines assoc
               :exit-opportunity-pipeline
               original-pipeline-id)
        ;; liabilities through :entity-updates subscription:
        (when (and @(r/track session/liabilities?)
                   (f-permissions/field-read-access?
                    :common/liabilities
                    :entity.type/exit-opportunity
                    original-pipeline-id))
          (events/subscribe-to-changes!
           {:entity      {:db/id exit-opportunity-id}
            :on-updates! events/updates-to-session-state})))
      :component-will-unmount
      (fn [_]
        (events/unsubscribe-changes! {:entity {:db/id exit-opportunity-id}})
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [exit-opportunity-id] :as _view-args}]
        (let [exit-opportunity (session/get-entity exit-opportunity-id)
              original-pipeline-id (:exit-opportunity/pipeline exit-opportunity)
              buyer-id (:exit-opportunity/buyer exit-opportunity)
              pipeline (session/get-entity original-pipeline-id)
              buyer (when buyer-id
                      (session/get-entity buyer-id))
              feature? @(r/track session/has-feature-access?
                                 :feature/exit-process-management)
              module? @(r/track session/exit-process-management?)
              header-fields []
              check-list-fields (resolve-check-list-fields pipeline exit-opportunity)]
          (cond
            (not @initialized?)
            [syncing/view]
            (not feature?)
            [:div
             {:class "m-4 text-xl"}
             (loc/tr :feature-x-not-enabled
                     [(loc/tr :feature/exit-process-management)])]
            (not module?)
            [:div
             {:class "m-4 text-xl"}
             (loc/tr :no-access-to-exit-process-management-module)]
            (or (:common/disabled? exit-opportunity)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not exit-opportunity)
                    (:common/disabled? exit-opportunity))
              [:div " "]
              [main-view {:state-atom state-atom
                          :error-atom error-atom
                          :notification-atom notification-atom
                          :events-pane-atom events-pane-atom
                          :view-state-atom view-state-atom
                          :entity-atom entity-atom
                          :temp-atom temp-atom
                          :exit-opportunity exit-opportunity
                          :pipeline pipeline
                          :buyer buyer
                          :header-fields header-fields
                          :check-list-fields check-list-fields
                          :capman-tenant? capman-tenant?}]))))})))

(defmethod routing/render-view :entity.type/exit-opportunity
  [{:keys [route-params _view-params]}]
  (let [exit-opportunity-id (-> route-params
                                :id
                                js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page exit-opportunity"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "exit-opportunity-viewer-id-" exit-opportunity-id)}
     [exit-opportunity-view {:exit-opportunity-id exit-opportunity-id}]]))
